import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded"
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded"
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createStockMovement,
  deleteStockMovement,
  getStockMovementById,
  getStockMovementFormData,
  getStockMovements,
  updateStockMovement,
} from "../../services/stockMovementService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  company_id: 1,
  product_id: "",
  movement_type: "",
  quantity: "",
  unit_cost: "",
  location_type: "warehouse",
  store_id: "",
  warehouse_id: "",
  reference_type: "",
  reference_id: "",
  external_reference: "",
  notes: "",
  created_by: 1,
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatQuantity(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  )
}


function formatAmount(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatDateTime(value) {
  if (!value) {
    return "-"
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-"
  }

  return date.toLocaleString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  )
}


function getMovementTypeLabel(value) {
  const labels = {
    opening_stock: "Stock initial",
    sale_out: "Sortie vente client",
    stock_order_out: "Sortie vers shop",
    stock_order_in: "Entrée shop",
    stock_in: "Entrée stock",
    stock_out: "Sortie stock",
    transfer_in: "Entrée transfert",
    transfer_out: "Sortie transfert",
    adjustment_in: "Correction +",
    adjustment_out: "Correction -",
    audit_correction_in: "Correction audit +",
    audit_correction_out: "Correction audit -",
    purchase_in: "Entrée achat",
    production_in: "Entrée production",
    manual: "Manuel",
  }

  return (
    labels[value] ||
    value ||
    "-"
  )
}


function isOutgoingMovement(value) {
  return [
    "sale_out",
    "stock_order_out",
    "stock_out",
    "transfer_out",
    "adjustment_out",
    "audit_correction_out",
  ].includes(value)
}


function isIncomingMovement(value) {
  return [
    "opening_stock",
    "stock_order_in",
    "stock_in",
    "transfer_in",
    "adjustment_in",
    "audit_correction_in",
    "purchase_in",
    "production_in",
  ].includes(value)
}


function getMovementColor(value) {
  if (
    isOutgoingMovement(value)
  ) {
    return "error"
  }

  if (
    isIncomingMovement(value)
  ) {
    return "success"
  }

  return "default"
}


function getMovementRowClass(value) {
  if (
    isOutgoingMovement(value)
  ) {
    return "stock-movement-row stock-movement-row--out"
  }

  if (
    isIncomingMovement(value)
  ) {
    return "stock-movement-row stock-movement-row--in"
  }

  return "stock-movement-row stock-movement-row--neutral"
}


function getLocationTypeLabel(value) {
  if (
    value === "store"
  ) {
    return "Shop"
  }

  if (
    value === "warehouse"
  ) {
    return "Entrepôt"
  }

  return "-"
}


function getLocationTypeChip(value) {
  if (
    value === "store"
  ) {
    return (
      <Chip
        label="Shop"
        size="small"
        color="primary"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  if (
    value === "warehouse"
  ) {
    return (
      <Chip
        label="Entrepôt"
        size="small"
        color="secondary"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  return (
    <Chip
      label="-"
      size="small"
      variant="outlined"
      sx={{
        height: 24,
      }}
    />
  )
}


function getReferenceLabel(value) {
  const labels = {
    SALE: "Vente",
    STOCK_ORDER: "Commande stock",
    STORE_RECONCILIATION: "Rapprochement",
    MANUAL: "Manuel",
  }

  return (
    labels[value] ||
    value ||
    "-"
  )
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.15,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.1,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            display: "grid",
            placeItems: "center",

            borderRadius: 2.5,

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.7,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 19,
                  sm: 21,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",

                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                minWidth: 0,

                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function MovementMobileCard({
  row,
  onEdit,
  onDelete,
}) {
  const isOut =
    isOutgoingMovement(
      row.movement_type
    )

  const isIn =
    isIncomingMovement(
      row.movement_type
    )

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          isOut
            ? "inset 4px 0 0 #ef4444, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : isIn
            ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #94a3b8, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,

          "&:last-child": {
            pb: 1.5,
          },
        }}
      >
        <Stack spacing={1.25}>
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "flex-start",

              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  overflowWrap:
                    "anywhere",
                }}
              >
                {row.product_name ||
                  "-"}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {row.product_code ||
                  "-"}
              </Typography>
            </Box>


            <Chip
              size="small"

              color={getMovementColor(
                row.movement_type
              )}

              label={getMovementTypeLabel(
                row.movement_type
              )}

              sx={{
                maxWidth: 150,

                "& .MuiChip-label":
                  {
                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",
                  },
              }}
            />
          </Box>


          <Box
            sx={{
              display: "flex",

              flexWrap: "wrap",

              alignItems: "center",

              gap: 0.7,
            }}
          >
            {getLocationTypeChip(
              row.location_type
            )}

            <Typography
              variant="body2"
              fontWeight={800}
              color="#334155"
            >
              {row.location_name ||
                "-"}
            </Typography>
          </Box>


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",

              gap: 0.8,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Quantité
              </Typography>

              <Typography
                fontWeight={900}
                color={
                  isOut
                    ? "error.main"
                    : isIn
                    ? "success.main"
                    : "#0f172a"
                }
              >
                {formatQuantity(
                  row.quantity
                )}
              </Typography>
            </Paper>


            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Coût / prix
              </Typography>

              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {row.unit_cost ===
                  null ||
                row.unit_cost ===
                  undefined
                  ? "-"
                  : formatAmount(
                      row.unit_cost
                    )}
              </Typography>
            </Paper>
          </Box>


          <Paper
            variant="outlined"
            sx={{
              p: 1,

              borderRadius: 2.5,

              bgcolor: "#fbfdff",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Référence
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
              color="#334155"
              sx={{
                mt: 0.15,
                overflowWrap:
                  "anywhere",
              }}
            >
              {getReferenceLabel(
                row.reference_type
              )}

              {row.reference_id
                ? ` #${row.reference_id}`
                : ""}

              {row.external_reference
                ? ` • ${row.external_reference}`
                : ""}
            </Typography>
          </Paper>


          <Typography
            variant="caption"
            color="text.secondary"
          >
            {formatDateTime(
              row.created_at
            )}
          </Typography>


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                {
                  xs: "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",
                },

              gap: 0.75,
            }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() =>
                onEdit(
                  row.id
                )
              }
            >
              Modifier
            </Button>


            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() =>
                onDelete(
                  row.id
                )
              }
            >
              Supprimer
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function StockMovementsPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    stockMovements,
    setStockMovements,
  ] = useState([])

  const [
    products,
    setProducts,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    warehouses,
    setWarehouses,
  ] = useState([])

  const [
    movementTypes,
    setMovementTypes,
  ] = useState([])


  // =======================================================
  // UI
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedMovement,
    setSelectedMovement,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    movementTypeFilter,
    setMovementTypeFilter,
  ] = useState("")

  const [
    locationFilter,
    setLocationFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize:
      ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // LOAD MOVEMENTS
  // =======================================================

  const loadStockMovements =
    async () => {
      try {
        setLoading(true)

        const data =
          await getStockMovements()

        setStockMovements(
          Array.isArray(data)
            ? data
            : []
        )
      } catch (error) {
        console.error(
          "Error loading stock movements:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les mouvements de stock."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getStockMovementFormData()

        setProducts(
          Array.isArray(
            data?.products
          )
            ? data.products
            : []
        )

        setStores(
          Array.isArray(
            data?.stores
          )
            ? data.stores
            : []
        )

        setWarehouses(
          Array.isArray(
            data?.warehouses
          )
            ? data.warehouses
            : []
        )

        setMovementTypes(
          Array.isArray(
            data?.movement_types
          )
            ? data.movement_types
            : []
        )
      } catch (error) {
        console.error(
          "Error loading stock movement form data:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les données du formulaire."
          ),
          "error"
        )
      }
    }


  useEffect(() => {
    loadStockMovements()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // FORM
  // =======================================================

  const resetForm = () => {
    setSelectedMovement(
      null
    )

    setFormData({
      ...defaultFormData,
    })
  }


  const openAddDialog =
    () => {
      resetForm()
      setOpen(true)
    }


  const openEditDialog =
    async (
      movementId
    ) => {
      try {
        const movement =
          await getStockMovementById(
            movementId
          )

        setSelectedMovement(
          movement
        )

        setFormData({
          company_id:
            movement.company_id ??
            1,

          product_id:
            movement.product_id ??
            "",

          movement_type:
            movement.movement_type ||
            "",

          quantity:
            movement.quantity !==
              null &&
            movement.quantity !==
              undefined
              ? Number(
                  movement.quantity
                )
              : "",

          unit_cost:
            movement.unit_cost !==
              null &&
            movement.unit_cost !==
              undefined
              ? Number(
                  movement.unit_cost
                )
              : "",

          location_type:
            movement.store_id
              ? "store"
              : "warehouse",

          store_id:
            movement.store_id ??
            "",

          warehouse_id:
            movement.warehouse_id ??
            "",

          reference_type:
            movement.reference_type ||
            "",

          reference_id:
            movement.reference_id !==
              null &&
            movement.reference_id !==
              undefined
              ? Number(
                  movement.reference_id
                )
              : "",

          external_reference:
            movement.external_reference ||
            "",

          notes:
            movement.notes ||
            "",

          created_by:
            movement.created_by ??
            1,
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger le mouvement."
          ),
          "error"
        )
      }
    }


  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  const handleChange =
    (event) => {
      const {
        name,
        value,
      } =
        event.target

      if (
        name ===
        "location_type"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            location_type:
              value,

            store_id:
              value === "store"
                ? previous.store_id
                : "",

            warehouse_id:
              value === "warehouse"
                ? previous.warehouse_id
                : "",
          })
        )

        return
      }


      setFormData(
        (previous) => ({
          ...previous,

          [name]:
            [
              "product_id",
              "store_id",
              "warehouse_id",
              "company_id",
              "created_by",
              "reference_id",
            ].includes(name)
              ? value === ""
                ? ""
                : Number(value)
              : [
                  "quantity",
                  "unit_cost",
                ].includes(name)
              ? value === ""
                ? ""
                : Number(value)
              : value,
        })
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !formData.product_id
    ) {
      showSnackbar(
        "Le produit est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      !formData.movement_type
    ) {
      showSnackbar(
        "Le type de mouvement est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      formData.quantity ===
        "" ||
      Number(
        formData.quantity
      ) === 0
    ) {
      showSnackbar(
        "La quantité est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      formData.location_type ===
        "store" &&
      !formData.store_id
    ) {
      showSnackbar(
        "Le shop est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      formData.location_type ===
        "warehouse" &&
      !formData.warehouse_id
    ) {
      showSnackbar(
        "L'entrepôt est obligatoire.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id:
          Number(
            formData.company_id ||
              1
          ),

        product_id:
          Number(
            formData.product_id
          ),

        movement_type:
          formData.movement_type,

        quantity:
          Number(
            formData.quantity
          ),

        unit_cost:
          formData.unit_cost ===
          ""
            ? null
            : Number(
                formData.unit_cost
              ),

        store_id:
          formData.location_type ===
            "store" &&
          formData.store_id
            ? Number(
                formData.store_id
              )
            : null,

        warehouse_id:
          formData.location_type ===
            "warehouse" &&
          formData.warehouse_id
            ? Number(
                formData.warehouse_id
              )
            : null,

        reference_type:
          formData.reference_type
            ?.trim() ||
          null,

        reference_id:
          formData.reference_id ===
          ""
            ? null
            : Number(
                formData.reference_id
              ),

        external_reference:
          formData.external_reference
            ?.trim() ||
          null,

        notes:
          formData.notes?.trim() ||
          null,

        created_by:
          Number(
            formData.created_by ||
              1
          ),
      }

      try {
        setSaving(true)

        if (
          selectedMovement?.id
        ) {
          await updateStockMovement(
            selectedMovement.id,
            payload
          )

          showSnackbar(
            "Mouvement de stock modifié avec succès."
          )
        } else {
          await createStockMovement(
            payload
          )

          showSnackbar(
            "Mouvement de stock créé avec succès."
          )
        }

        /*
         * Fermeture directe :
         * handleClose() ne ferme pas pendant saving === true.
         */
        setOpen(false)
        resetForm()

        await loadStockMovements()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible d'enregistrer le mouvement."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (
      movementId
    ) => {
      const confirmed =
        window.confirm(
          "Voulez-vous vraiment supprimer ce mouvement de stock ?"
        )

      if (!confirmed) {
        return
      }

      try {
        await deleteStockMovement(
          movementId
        )

        showSnackbar(
          "Mouvement supprimé avec succès."
        )

        await loadStockMovements()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de supprimer le mouvement."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filteredStockMovements =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return stockMovements.filter(
        (movement) => {
          const matchesSearch =
            keyword === "" ||
            [
              movement.product_code,
              movement.product_name,
              movement.movement_type,
              getMovementTypeLabel(
                movement.movement_type
              ),
              movement.location_name,
              getLocationTypeLabel(
                movement.location_type
              ),
              movement.reference_type,
              getReferenceLabel(
                movement.reference_type
              ),
              movement.external_reference,
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          const matchesMovementType =
            movementTypeFilter ===
              "" ||
            movement.movement_type ===
              movementTypeFilter

          const matchesLocation =
            locationFilter ===
              "" ||
            movement.location_type ===
              locationFilter

          return (
            matchesSearch &&
            matchesMovementType &&
            matchesLocation
          )
        }
      )
    }, [
      stockMovements,
      search,
      movementTypeFilter,
      locationFilter,
    ])


  // =======================================================
  // STATS
  // =======================================================

  const stats =
    useMemo(() => {
      return filteredStockMovements.reduce(
        (
          accumulator,
          movement
        ) => {
          accumulator.total +=
            1

          if (
            movement.location_type ===
            "warehouse"
          ) {
            accumulator.warehouse +=
              1
          }

          if (
            movement.location_type ===
            "store"
          ) {
            accumulator.store +=
              1
          }

          if (
            [
              "sale_out",
              "stock_order_out",
              "transfer_out",
            ].includes(
              movement.movement_type
            )
          ) {
            accumulator.salesOut +=
              1

            accumulator.salesOutQty +=
              Math.abs(
                toNumber(
                  movement.quantity
                )
              )
          }

          return accumulator
        },
        {
          total: 0,
          warehouse: 0,
          store: 0,
          salesOut: 0,
          salesOutQty: 0,
        }
      )
    }, [
      filteredStockMovements,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,

        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    )
  }, [
    search,
    movementTypeFilter,
    locationFilter,
  ])


  const paginatedMobileRows =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredStockMovements.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredStockMovements,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredStockMovements.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    filteredStockMovements.length,
    paginationModel.page,
  ])


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters = () => {
    setSearch("")
    setMovementTypeFilter("")
    setLocationFilter("")

    setPaginationModel({
      page: 0,
      pageSize:
        ROWS_PER_PAGE,
    })
  }


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "product",

          headerName:
            "Produit",

          minWidth: 205,

          flex: 1.25,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.16}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.86rem",
                    fontWeight: 900,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.product_name ||
                    ""
                  }
                >
                  {params.row.product_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.row.product_code ||
                    "-"}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "movement_type",

          headerName:
            "Mouvement",

          minWidth: 175,

          flex: 1.05,

          renderCell:
            (params) => (
              <Chip
                size="small"

                color={getMovementColor(
                  params.value
                )}

                label={getMovementTypeLabel(
                  params.value
                )}

                sx={{
                  maxWidth: "100%",

                  "& .MuiChip-label":
                    {
                      overflow:
                        "hidden",

                      textOverflow:
                        "ellipsis",
                    },
                }}
              />
            ),
        },


        {
          field:
            "location",

          headerName:
            "Emplacement",

          minWidth: 210,

          flex: 1.2,

          sortable: false,

          renderCell:
            (params) => (
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,

                  display: "flex",

                  alignItems: "center",

                  gap: 0.85,
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  {getLocationTypeChip(
                    params.row.location_type
                  )}
                </Box>

                <Typography
                  sx={{
                    minWidth: 0,

                    color: "#334155",

                    fontSize: "0.83rem",

                    fontWeight: 800,

                    lineHeight: 1.25,

                    overflow: "hidden",

                    textOverflow:
                      "ellipsis",

                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.location_name ||
                    ""
                  }
                >
                  {params.row.location_name ||
                    "-"}
                </Typography>
              </Box>
            ),
        },


        {
          field:
            "quantity_cost",

          headerName:
            "Quantité / coût",

          width: 145,

          sortable: false,

          renderCell:
            (params) => {
              const isOut =
                isOutgoingMovement(
                  params.row.movement_type
                )

              const isIn =
                isIncomingMovement(
                  params.row.movement_type
                )

              return (
                <Stack
                  spacing={0.12}

                  alignItems="flex-end"

                  sx={{
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      color: isOut
                        ? "#dc2626"
                        : isIn
                        ? "#15803d"
                        : "#0f172a",

                      fontSize:
                        "0.86rem",

                      fontWeight:
                        900,

                      lineHeight:
                        1.2,
                    }}
                  >
                    {formatQuantity(
                      params.row.quantity
                    )}
                  </Typography>

                  <Typography
                    variant="caption"

                    sx={{
                      color:
                        "#64748b",

                      fontWeight:
                        700,

                      lineHeight:
                        1.15,
                    }}
                  >
                    {params.row.unit_cost ===
                      null ||
                    params.row.unit_cost ===
                      undefined
                      ? "Coût : -"
                      : `Coût : ${formatAmount(
                          params.row.unit_cost
                        )}`}
                  </Typography>
                </Stack>
              )
            },
        },


        {
          field:
            "reference",

          headerName:
            "Référence",

          minWidth: 185,

          flex: 1.05,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.12}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#334155",
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    getReferenceLabel(
                      params.row.reference_type
                    )
                  }
                >
                  {getReferenceLabel(
                    params.row.reference_type
                  )}

                  {params.row.reference_id
                    ? ` #${params.row.reference_id}`
                    : ""}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.15,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.external_reference ||
                    ""
                  }
                >
                  {params.row.external_reference ||
                    "Aucun code source"}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "created_at",

          headerName:
            "Date",

          width: 145,

          renderCell:
            (params) => (
              <Typography
                sx={{
                  color: "#475569",
                  fontSize: "0.79rem",
                  fontWeight: 700,
                  lineHeight: 1.25,
                }}
              >
                {formatDateTime(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field:
            "actions",

          headerName:
            "Actions",

          width: 178,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Stack
                direction="row"
                spacing={0.55}

                sx={{
                  width: "100%",
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  disableElevation

                  onClick={() =>
                    openEditDialog(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 74,
                    px: 1,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Modifier
                </Button>


                <Button
                  size="small"
                  variant="outlined"
                  color="error"

                  onClick={() =>
                    handleDelete(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 82,
                    px: 0.9,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Supprimer
                </Button>
              </Stack>
            ),
        },
      ],
      []
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,

              bgcolor: "#ffffff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Mouvements stock
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 800,
              }}
            >
              Suivi des entrées, sorties, livraisons clients,
              transferts et corrections de stock.
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation

            onClick={
              openAddDialog
            }

            sx={{
              minWidth: {
                xs: "100%",
                sm: 190,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouveau mouvement
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"
          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,
          }}
        >
          Les ventes des agents commerciaux sortent le stock
          depuis les entrepôts Entreprise principale.
          Distributeur principal et Structure secondaire
          reçoivent le stock depuis les entrepôts Entreprise
          principale, puis vendent depuis leurs shops.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.1,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Mouvements"

            value={
              stats.total
            }

            caption="Mouvements affichés"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <Inventory2RoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Entrepôts"

            value={
              stats.warehouse
            }

            caption="Mouvements liés aux entrepôts"

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <WarehouseRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Shops"

            value={
              stats.store
            }

            caption="Mouvements liés aux shops"

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <StorefrontRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Sorties ventes"

            value={
              formatQuantity(
                stats.salesOutQty
              )
            }

            caption={`${stats.salesOut} mouvement(s) de sortie`}

            accent="#dc2626"

            background="rgba(220, 38, 38, 0.09)"

            icon={
              <TrendingDownRoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(0, 1fr) 240px 190px 135px",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"

              label="Recherche"

              placeholder="Produit, emplacement, référence..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select
              fullWidth
              size="small"

              label="Type mouvement"

              value={
                movementTypeFilter
              }

              onChange={(
                event
              ) =>
                setMovementTypeFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {movementTypes.map(
                (type) => (
                  <MenuItem
                    key={
                      type.value
                    }

                    value={
                      type.value
                    }
                  >
                    {type.label ||
                      getMovementTypeLabel(
                        type.value
                      )}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select
              fullWidth
              size="small"

              label="Type de lieu"

              value={
                locationFilter
              }

              onChange={(
                event
              ) =>
                setLocationFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="store">
                Shop
              </MenuItem>

              <MenuItem value="warehouse">
                Entrepôt
              </MenuItem>
            </TextField>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                width: "100%",

                minWidth: 0,

                whiteSpace:
                  "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des mouvements stock
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Historique des entrées, sorties, transferts et
                corrections
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredStockMovements.length} mouvement${
                filteredStockMovements.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflowX: "auto",

                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  minWidth: 1160,
                }}
              >
                <DataGrid
                  autoHeight

                  rows={
                    filteredStockMovements
                  }

                  columns={
                    columns
                  }

                  loading={
                    loading
                  }

                  disableRowSelectionOnClick

                  disableColumnSelector

                  pagination

                  paginationMode="client"

                  paginationModel={
                    paginationModel
                  }

                  onPaginationModelChange={(
                    model
                  ) =>
                    setPaginationModel({
                      page:
                        model.page,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  }

                  pageSizeOptions={[
                    ROWS_PER_PAGE,
                  ]}

                  rowHeight={70}

                  columnHeaderHeight={50}

                  getRowClassName={(
                    params
                  ) =>
                    getMovementRowClass(
                      params.row.movement_type
                    )
                  }

                  localeText={{
                    noRowsLabel:
                      "Aucun mouvement de stock",

                    noResultsOverlayLabel:
                      "Aucun résultat",

                    MuiTablePagination:
                      {
                        labelRowsPerPage:
                          "",

                        labelDisplayedRows:
                          ({
                            from,
                            to,
                            count,
                          }) =>
                            `${from}–${to} sur ${count}`,
                      },
                  }}

                  sx={{
                    border: 0,

                    width: "100%",

                    color: "#334155",

                    fontSize: "0.83rem",

                    bgcolor:
                      "#ffffff",

                    "& .MuiDataGrid-columnHeaders":
                      {
                        bgcolor:
                          "#f8fafc",

                        borderBottom:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiDataGrid-columnHeader":
                      {
                        bgcolor:
                          "#f8fafc",

                        px: 1.25,

                        borderRight:
                          "1px solid rgba(226, 232, 240, 0.60)",
                      },

                    "& .MuiDataGrid-columnHeader:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeaderTitle":
                      {
                        color:
                          "#334155",

                        fontSize:
                          "0.76rem",

                        fontWeight: 900,

                        letterSpacing:
                          "0.025em",
                      },

                    "& .MuiDataGrid-columnSeparator":
                      {
                        color:
                          "rgba(148, 163, 184, 0.18)",
                      },

                    "& .MuiDataGrid-cell":
                      {
                        px: 1.25,

                        minWidth: 0,

                        display: "flex",

                        alignItems:
                          "center",

                        overflow:
                          "hidden",

                        borderBottom:
                          "1px solid #edf2f7",

                        borderRight:
                          "1px solid rgba(241, 245, 249, 0.85)",
                      },

                    "& .MuiDataGrid-cell:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .stock-movement-row":
                      {
                        bgcolor:
                          "#ffffff",

                        transition:
                          "background-color 0.16s ease, box-shadow 0.16s ease",
                      },

                    "& .stock-movement-row--in":
                      {
                        boxShadow:
                          "inset 4px 0 0 #22c55e",

                        bgcolor:
                          "rgba(240, 253, 244, 0.32)",
                      },

                    "& .stock-movement-row--out":
                      {
                        boxShadow:
                          "inset 4px 0 0 #ef4444",

                        bgcolor:
                          "rgba(254, 242, 242, 0.32)",
                      },

                    "& .stock-movement-row--neutral":
                      {
                        boxShadow:
                          "inset 4px 0 0 #cbd5e1",
                      },

                    "& .stock-movement-row:hover":
                      {
                        bgcolor:
                          "#f8fbff !important",

                        boxShadow:
                          "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                      },

                    "& .MuiDataGrid-footerContainer":
                      {
                        minHeight: 52,

                        bgcolor:
                          "#fbfdff",

                        borderTop:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiTablePagination-toolbar":
                      {
                        minHeight: 52,

                        px: 1.25,
                      },

                    "& .MuiTablePagination-displayedRows":
                      {
                        color:
                          "#475569",

                        fontSize:
                          "0.78rem",

                        fontWeight: 800,
                      },

                    "& .MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },

                    "& .MuiTablePagination-input":
                      {
                        display: "none",
                      },

                    "& .MuiDataGrid-virtualScroller":
                      {
                        overflowX:
                          "auto",
                      },

                    "& .MuiDataGrid-scrollbar--horizontal":
                      {
                        height:
                          "8px",
                      },
                  }}
                />
              </Box>
            </Box>
          ) : (

            /* =================================================
               MOBILE / TABLET
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.1,
                  sm: 1.25,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des mouvements...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun mouvement trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map(
                    (row) => (
                      <MovementMobileCard
                        key={
                          row.id
                        }

                        row={
                          row
                        }

                        onEdit={
                          openEditDialog
                        }

                        onDelete={
                          handleDelete
                        }
                      />
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredStockMovements.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "92dvh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.3,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedMovement
              ? "Modifier le mouvement stock"
              : "Nouveau mouvement stock"}
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedMovement
              ? "Modifiez les informations de ce mouvement de stock."
              : "Enregistrez une entrée, une sortie, un transfert ou une correction."}
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >

            {/* PRODUCT + TYPE */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Mouvement
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Produit"

                  name="product_id"

                  value={
                    formData.product_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }
                >
                  {products.map(
                    (product) => (
                      <MenuItem
                        key={
                          product.id
                        }

                        value={
                          product.id
                        }
                      >
                        {product.code} -{" "}
                        {product.name}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select
                  size="small"

                  label="Type mouvement"

                  name="movement_type"

                  value={
                    formData.movement_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }
                >
                  {movementTypes.map(
                    (type) => (
                      <MenuItem
                        key={
                          type.value
                        }

                        value={
                          type.value
                        }
                      >
                        {type.label ||
                          getMovementTypeLabel(
                            type.value
                          )}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>
            </Paper>


            {/* QUANTITY + COST */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Quantité et valeur
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Quantité"

                  name="quantity"

                  type="number"

                  value={
                    formData.quantity
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      step: 0.01,
                    },
                  }}
                />


                <TextField
                  size="small"

                  label="Coût / prix unitaire"

                  name="unit_cost"

                  type="number"

                  value={
                    formData.unit_cost
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />
              </Box>
            </Paper>


            {/* LOCATION */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Emplacement
              </Typography>


              <Stack spacing={1.3}>
                <TextField
                  select
                  size="small"

                  label="Type d'emplacement"

                  name="location_type"

                  value={
                    formData.location_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="warehouse">
                    Entrepôt
                  </MenuItem>

                  <MenuItem value="store">
                    Shop
                  </MenuItem>
                </TextField>


                {formData.location_type ===
                "store" ? (
                  <TextField
                    select
                    size="small"

                    label="Shop"

                    name="store_id"

                    value={
                      formData.store_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {stores.map(
                      (store) => (
                        <MenuItem
                          key={
                            store.id
                          }

                          value={
                            store.id
                          }
                        >
                          {store.code} -{" "}
                          {store.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                ) : (
                  <TextField
                    select
                    size="small"

                    label="Entrepôt"

                    name="warehouse_id"

                    value={
                      formData.warehouse_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {warehouses.map(
                      (warehouse) => (
                        <MenuItem
                          key={
                            warehouse.id
                          }

                          value={
                            warehouse.id
                          }
                        >
                          {warehouse.code} -{" "}
                          {warehouse.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}
              </Stack>
            </Paper>


            {/* REFERENCE */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Référence métier
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,

                  mb: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Type référence"

                  name="reference_type"

                  value={
                    formData.reference_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Aucune
                  </MenuItem>

                  <MenuItem value="SALE">
                    Vente / demande client
                  </MenuItem>

                  <MenuItem value="STOCK_ORDER">
                    Commande stock
                  </MenuItem>

                  <MenuItem value="STORE_RECONCILIATION">
                    Rapprochement
                  </MenuItem>

                  <MenuItem value="MANUAL">
                    Manuel
                  </MenuItem>
                </TextField>


                <TextField
                  size="small"

                  label="ID référence"

                  name="reference_id"

                  type="number"

                  value={
                    formData.reference_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>


              <TextField
                size="small"

                label="Code référence externe"

                name="external_reference"

                value={
                  formData.external_reference
                }

                onChange={
                  handleChange
                }

                fullWidth

                disabled={
                  saving
                }

                placeholder="Exemple : ORD-0001, SALE-0007..."
              />
            </Paper>


            {/* NOTES */}

            <TextField
              label="Notes"

              name="notes"

              value={
                formData.notes
              }

              onChange={
                handleChange
              }

              fullWidth

              multiline

              minRows={3}

              disabled={
                saving
              }
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"
            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedMovement
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          handleCloseSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbar.severity
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default StockMovementsPage
